import { z } from 'zod';
import { devicePlatformSchema, deviceSummarySchema } from './device.js';
/**
 * Заголовок double-submit CSRF-токена. Единственный источник истины и для
 * сервера (проверка), и для Web-клиента (какой заголовок проставить).
 */
export const CSRF_HEADER_NAME = 'x-csrf-token';
/**
 * Версия клиента, как он сам её называет.
 *
 * Едет заголовком, а не полем при регистрации: устройство регистрируется один
 * раз, а обновляется потом много раз, и версия, записанная при знакомстве,
 * через месяц описывает давно заменённое приложение. Заголовок же приходит с
 * каждым запросом — и всегда про то приложение, которое работает прямо сейчас.
 */
export const APP_VERSION_HEADER_NAME = 'x-app-version';
/**
 * Роли первой версии. Группы — свободные метки для политик доступа,
 * они назначаются администратором и не создаются клиентом.
 */
export const userRoleSchema = z.enum(['admin', 'user']);
export const usernameSchema = z
    .string()
    .trim()
    .min(3)
    .max(64)
    .regex(/^[a-zA-Z0-9._-]+$/, 'допустимы латиница, цифры, точка, дефис и подчёркивание');
/**
 * Минимальная длина пароля 10 символов: сервис закрыт login/password gate,
 * а перебор ограничивается rate limit, но не заменяет длину пароля.
 */
export const passwordSchema = z.string().min(10).max(256);
export const sessionUserSchema = z.object({
    id: z.string(),
    username: z.string(),
    displayName: z.string(),
    /** Эмодзи профиля; `null` — рисуется инициал. */
    avatar: z.string().max(8).nullable().default(null),
    /**
     * Загруженная картинка профиля — адрес на нашем сервере; `null` — картинки
     * нет. Отдельно от `avatar`: эмодзи остаётся запасным вариантом, а адрес
     * меняется при каждой загрузке (в нём метка версии), иначе клиент показывал бы
     * старую картинку из кэша.
     */
    avatarUrl: z.string().max(200).nullable().default(null),
    role: userRoleSchema,
    groups: z.array(z.string()),
    disabled: z.boolean(),
    createdAt: z.string(),
});
export const loginRequestSchema = z.object({
    username: z.string().trim().min(1).max(64),
    password: z.string().min(1).max(256),
    /**
     * Клиент может передать описание устройства при первом входе.
     * Никаких идентификаторов рекламы и лишней телеметрии — только то,
     * что администратор увидит в списке устройств (DEVICE_ENROLLMENT.md).
     */
    device: z
        .object({
        platform: devicePlatformSchema.default('web'),
        label: z.string().trim().max(80).optional(),
        model: z.string().trim().max(80).optional(),
        manufacturer: z.string().trim().max(80).optional(),
        osVersion: z.string().trim().max(40).optional(),
        appVersion: z.string().trim().max(40).optional(),
        abi: z.string().trim().max(40).optional(),
    })
        .optional(),
});
/**
 * Ответ сессии. `deviceToken` возвращается ТОЛЬКО не-браузерным клиентам
 * (Android TV): в браузере persistent credential живёт в HttpOnly cookie
 * и в JavaScript не попадает.
 */
export const sessionResponseSchema = z.object({
    user: sessionUserSchema,
    device: deviceSummarySchema,
    /** Значение double-submit CSRF-токена для изменяющих запросов. */
    csrfToken: z.string(),
    /** Момент истечения короткоживущего access-токена, не всей сессии. */
    accessExpiresAt: z.string(),
    deviceToken: z.string().nullable(),
});
/**
 * Состояние текущего клиента до входа. Отдаётся без авторизации и
 * намеренно не раскрывает ничего, кроме того, что нужно экрану входа.
 */
export const authStateSchema = z.object({
    authenticated: z.boolean(),
    /** Требуется ли одобрение устройства администратором. */
    deviceApprovalRequired: z.boolean(),
    /**
     * Требуется ли вход, чтобы вообще открыть кинотеатр.
     *
     * Решает сервер, а не клиент: в рабочем контуре (`ENV=PROD`) дверь заперта, в
     * разработке — нет, и зашивать это в веб-приложение значит держать два разных
     * ответа на один вопрос.
     */
    loginRequired: z.boolean().default(false),
    /**
     * Почему это устройство не впустили без формы входа — для страницы входа и
     * для разбора жалоб «одобрил, а оно всё равно просит пароль». Не секрет:
     * говорит только то, что устройство и так о себе знает. `null` — впустили
     * либо причин нет.
     *
     * - `no-device` — браузер не прислал запись устройства (cookie нет);
     * - `unknown-device` — прислал, но такой записи на сервере нет;
     * - `pending`, `blocked`, `revoked` — состояние устройства;
     * - `no-owner` — одобрено, но владелец не назначен;
     * - `owner-unavailable` — владелец удалён или отключён;
     * - `password-required` — одобрено, но это обычный браузер: ему нужен пароль.
     */
    signInBlocked: z
        .enum([
        'no-device', 'unknown-device', 'pending', 'blocked', 'revoked',
        'no-owner', 'owner-unavailable', 'password-required',
    ])
        .nullable()
        .default(null),
    /**
     * Какие cookie входа дошли до сервера с этим запросом — только «есть/нет»,
     * без значений. Для разбора: браузер телевизора, потерявший cookie, снаружи
     * неотличим от устройства, которое портал не знает.
     */
    cookies: z.object({ device: z.boolean(), session: z.boolean() }).optional(),
    /**
     * Администратор ждёт новое устройство (режим ожидания): страница входа сама
     * объявляет своё устройство серверу (`POST /devices/waiting/join`) и показывает
     * код. Сервер прежней версии поля не знает — тогда `false`.
     */
    deviceWaiting: z.boolean().default(false),
    /** Есть ли в системе хотя бы один администратор (первый запуск). */
    setupCompleted: z.boolean(),
    device: deviceSummarySchema.nullable(),
    user: sessionUserSchema.nullable(),
    csrfToken: z.string().nullable(),
});
export const refreshRequestSchema = z.object({
    /** Для Android TV: persistent credential из защищённого хранилища. */
    deviceToken: z.string().min(16).max(512).optional(),
});
export const changePasswordRequestSchema = z.object({
    currentPassword: z.string().min(1).max(256),
    newPassword: passwordSchema,
});
//# sourceMappingURL=auth.js.map